import { zodResolver } from '@hookform/resolvers/zod'
import {
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  Loader2,
  XCircle,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'
import { z } from 'zod'
import { GoogleSignInButton } from '@/components/auth/GoogleSignInButton'
import type { CreateAccountStepProps } from '@/components/registration/steps/CreateAccountStep.types'
import { Alert } from '@/components/ui/alert'
import { BrandLogo } from '@/components/ui/brand-logo'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { useGoogleSSOEnabled } from '@/hooks/useGoogleSSOEnabled'
import { useMixpanel } from '@/hooks/useMixpanel'
import { useWelcomeTransition } from '@/hooks/useWelcomeTransition'
import { useNavigationManager } from '@/lib/navigation'
import { setRememberedEmail } from '@/lib/rememberedEmail'
import { routePaths } from '@/lib/router'
import type { ApiError } from '@/services/api'
import {
  authService,
  type LoginResponse,
  type RegisterRequest,
  type RegisterResponse,
} from '@/services/auth'
import { invitationService } from '@/services/invitations'
import {
  REGISTRATION_PAGE_SIGN_UP_EVENT,
  REGISTRATION_PAGE_VISIT_EVENT,
} from '@/services/mixpanel'
import { useAuthStore } from '@/stores/auth'

// Validation schema (from RegisterPage)
const registerSchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .min(2, 'Name must be at least 2 characters'),
  company: z
    .string()
    .min(1, 'Company name is required')
    .min(2, 'Company name must be at least 2 characters'),
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
  password: z
    .string()
    .min(1, 'Password is required')
    .min(8, 'Minimum 8 characters')
    .regex(
      /(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      'Password must contain at least one uppercase letter, one lowercase letter, and one number'
    ),
})

type RegisterFormData = z.infer<typeof registerSchema>

interface InvitationInfo {
  email: string
  organizationName: string
  inviterName: string
  expiresAt: string
}

type InvitationValidationState =
  | 'loading'
  | 'valid'
  | 'expired'
  | 'invalid'
  | 'accepted'
  | 'cancelled'
  | 'none'

const INVITATION_ERROR_STATES = {
  expired: {
    title: 'Invitation Expired',
    message:
      'This invitation link has expired. Please request a new invitation from your team administrator.',
  },
  accepted: {
    title: 'Invitation Already Used',
    message:
      "This invitation has already been accepted. If you're the invited user, you can log in directly.",
  },
  cancelled: {
    title: 'Invitation Cancelled',
    message:
      'This invitation has been cancelled. Please request a new invitation from your team administrator.',
  },
  invalid: {
    title: 'Invalid Invitation',
    message:
      'The invitation link is invalid or has been used. Please request a new invitation.',
  },
} as const

type InvitationErrorState = keyof typeof INVITATION_ERROR_STATES

const isInvitationValidationState = (
  status: unknown
): status is InvitationValidationState => {
  return (
    status === 'loading' ||
    status === 'valid' ||
    status === 'expired' ||
    status === 'invalid' ||
    status === 'accepted' ||
    status === 'cancelled' ||
    status === 'none'
  )
}

export function CreateAccountStep({ returnTo }: CreateAccountStepProps) {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const navigationManager = useNavigationManager(navigate, location)
  const googleSSOEnabled = useGoogleSSOEnabled()
  const invitationToken = searchParams.get('invitation_token')
  const { setTokens, setUser, setOrganization, setRole, setAuthState } =
    useAuthStore()
  const welcomeTransition = useWelcomeTransition()

  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [emailAlreadyExists, setEmailAlreadyExists] = useState(false)
  const [invitationInfo, setInvitationInfo] = useState<InvitationInfo | null>(
    null
  )
  const [loadingInvitation, setLoadingInvitation] = useState(!!invitationToken)
  const [invitationState, setInvitationState] =
    useState<InvitationValidationState>(invitationToken ? 'loading' : 'none')
  const [activeSlide, setActiveSlide] = useState(0)
  const { track } = useMixpanel()

  // Track registration page visit
  useEffect(() => {
    track(REGISTRATION_PAGE_VISIT_EVENT)
  }, [track])

  const form = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {},
  })

  // Fetch invitation details if token is provided
  useEffect(() => {
    if (!invitationToken) {
      setLoadingInvitation(false)
      return
    }

    const fetchInvitationInfo = async () => {
      try {
        const response =
          await invitationService.getInvitationDetails(invitationToken)

        // Handle based on invitation validity and status
        if (response.valid) {
          setInvitationInfo(response)
          // Pre-fill email field and make it readonly
          form.setValue('email', response.email)
          // Pre-fill company field with organization name
          form.setValue('company', response.organizationName)
          setInvitationState('valid')
        } else {
          // Invalid invitation - set state based on status
          const status = response.status
          if (isInvitationValidationState(status)) {
            setInvitationState(status)
          } else {
            // Fallback for unexpected status values
            console.warn('Unexpected invitation status:', status)
            setInvitationState('invalid')
          }
        }
      } catch (error) {
        console.error('Failed to fetch invitation details:', error)
        setInvitationState('invalid')
      } finally {
        setLoadingInvitation(false)
      }
    }

    fetchInvitationInfo()
  }, [invitationToken, form])

  // Auto-advance slides every 5 seconds, reset to first after last
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveSlide((prev) => {
        // If at last slide (index 2), reset to first
        if (prev === 2) {
          return 0
        }
        return prev + 1
      })
    }, 5000)
    return () => clearInterval(timer)
  }, [])

  const onSubmit = async (data: RegisterFormData) => {
    track(REGISTRATION_PAGE_SIGN_UP_EVENT)
    setIsLoading(true)
    setSubmitError(null)
    setEmailAlreadyExists(false)

    try {
      // Split the name into firstName and lastName for the API
      const nameParts = data.name.trim().split(' ')
      const firstName = nameParts[0] || ''
      const lastName = nameParts.slice(1).join(' ') || ''

      const registerData: RegisterRequest & { invitationToken?: string } = {
        firstName,
        lastName,
        email: data.email.trim().toLowerCase(),
        password: data.password,
        confirmPassword: data.password,
        organizationName: data.company.trim(),
        agreeToTerms: true, // User agrees by signing up (terms displayed in UI)
      }

      // Add invitation token if available
      if (invitationToken) {
        registerData.invitationToken = invitationToken
      }

      const response = await authService.register(registerData)

      // Pre-fill login email for when the user eventually logs out
      setRememberedEmail(data.email.trim().toLowerCase())

      await completeRegistration(response)
    } catch (error) {
      const apiErr = error as ApiError
      if (apiErr?.code === 'email_already_exists') {
        setEmailAlreadyExists(true)
      } else {
        const fallback = 'Registration failed. Please try again.'
        setSubmitError(apiErr?.message || fallback)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const completeRegistration = async (
    response: RegisterResponse | LoginResponse
  ) => {
    await welcomeTransition.run(() => {
      setTokens({
        accessToken: response.accessToken,
        refreshToken: response.refreshToken,
        expiresAt: Date.now() + response.expiresIn * 1000,
      })
      setUser(response.user)
      setOrganization(response.organization)
      setRole(response.role)
      setAuthState('authenticated')

      navigationManager.redirectAfterLogin(response.user, returnTo)
    })
  }

  const handleGoogleRegistration = async (accessToken: string) => {
    const response = await authService.googleSignIn(accessToken)

    await completeRegistration(response)
  }

  const loading = isLoading || loadingInvitation

  // Watch form values to determine if form is complete
  const watchedValues = form.watch()
  const isFormComplete =
    !!watchedValues.name?.trim() &&
    !!watchedValues.company?.trim() &&
    !!watchedValues.email?.trim() &&
    !!watchedValues.password?.trim()

  const isInvitationErrorState = (
    state: InvitationValidationState
  ): state is InvitationErrorState => {
    return state in INVITATION_ERROR_STATES
  }

  // Helper to determine if field should be readonly/disabled for invitations
  const isInvitationField = !!invitationToken
  const getInvitationFieldProps = () => ({
    disabled: loading || isInvitationField,
    readOnly: isInvitationField,
    className: `h-9 rounded-full border-border px-3 py-1 text-[14px] shadow-sm focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:outline-none transition-[color,box-shadow] ${
      isInvitationField ? 'bg-muted cursor-not-allowed' : 'bg-background'
    }`,
  })

  // Loading state while validating invitation
  if (loadingInvitation) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-secondary">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center space-y-6">
            <Loader2 className="mx-auto h-8 w-8 animate-spin" />
            <p className="text-muted-foreground">Validating invitation...</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Invitation error states - replace form entirely
  if (isInvitationErrorState(invitationState)) {
    const errorConfig = INVITATION_ERROR_STATES[invitationState]

    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-secondary">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center space-y-6">
            <div className="mx-auto w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
              <XCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>

            <div className="space-y-2">
              <h3 className="text-xl font-semibold">{errorConfig.title}</h3>
              <p className="text-muted-foreground">{errorConfig.message}</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Link to={routePaths.login} className="w-full">
                <Button className="w-full">Go to Login</Button>
              </Link>
              <Link to={routePaths.register} className="w-full">
                <Button variant="outline" className="w-full">
                  Create Account
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Content data for the 3 animated states
  const contentStates = [
    {
      title: 'Built for your brand',
      description:
        'Powered by your catalog and rules to deliver curated, relevant recommendations.',
      bgColor: 'bg-blue-100',
      content: (
        <div className="relative w-full h-full flex items-center justify-center p-6">
          <img
            src="/assets/signup/state1.png"
            alt="AI product recommendation chat interface"
            className="w-full h-full object-contain"
          />
        </div>
      ),
    },
    {
      title: 'Boosting CVR',
      description:
        'Turn questions into confident purchases so fewer shoppers stall or drop off.',
      bgColor: 'bg-pink-100',
      content: (
        <div className="relative w-full h-full flex items-center justify-center p-6">
          <img
            src="/assets/signup/state2.png"
            alt="CVR analytics and conversion metrics"
            className="w-full h-full object-contain"
          />
        </div>
      ),
    },
    {
      title: 'Launchable in Minutes',
      description:
        'Get your AI assistant live with a quick, lightweight setup.',
      bgColor: 'bg-teal-100',
      content: (
        <div className="relative w-full h-full flex items-center justify-center p-6">
          <img
            src="/assets/signup/state3.png"
            alt="Quick setup product recommendation interface"
            className="w-full h-full object-contain"
          />
        </div>
      ),
    },
  ]

  const nextSlide = () => {
    setActiveSlide((prev) => {
      // If at last slide (index 2), reset to first
      if (prev === 2) {
        return 0
      }
      return prev + 1
    })
  }

  const prevSlide = () => {
    setActiveSlide((prev) => {
      // Don't go below 0
      if (prev === 0) {
        return 0
      }
      return prev - 1
    })
  }

  // Split-screen layout
  return (
    <div className="min-h-screen flex gap-6 p-4 md:p-6 bg-background">
      {/* Left side - Sign up form */}
      <div className="flex-1 flex flex-col justify-center items-center">
        <div className="absolute left-4 top-4 md:left-6 md:top-6">
          <BrandLogo />
        </div>

        <div className="w-full max-w-[400px] px-2 md:px-0 space-y-12">
          {/* Header */}
          <div className="space-y-3 text-left">
            <h1 className="font-medium text-[30px] leading-[36px] text-foreground">
              {isInvitationField ? 'Complete Your Invitation' : 'Get started'}
            </h1>
            <p className="text-[14px] leading-[20px] text-muted-foreground">
              {isInvitationField ? (
                <>
                  You've been invited to join{' '}
                  <strong>{invitationInfo?.organizationName}</strong>.
                </>
              ) : (
                <>
                  Already have an account?{' '}
                  <Link
                    to={
                      returnTo
                        ? `${routePaths.login}?returnTo=${encodeURIComponent(returnTo)}`
                        : routePaths.login
                    }
                    className="text-blue-600"
                  >
                    Log In
                  </Link>
                </>
              )}
            </p>
          </div>

          {/* Invitation Banner */}
          {isInvitationField && invitationInfo && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                🎉 You're joining{' '}
                <strong>{invitationInfo.organizationName}</strong>
              </p>
            </div>
          )}

          {/* Google Sign-Up (skip for invitation flows) */}
          {!isInvitationField && googleSSOEnabled && (
            <div className="space-y-4">
              <GoogleSignInButton
                label="Sign up with Google"
                onRegistrationSuccess={handleGoogleRegistration}
              />
              <div className="flex items-center gap-3">
                <div className="h-px flex-1 bg-border" />
                <span className="text-[13px] text-muted-foreground">or</span>
                <div className="h-px flex-1 bg-border" />
              </div>
            </div>
          )}

          {/* Form */}
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {/* Name and Company in a row */}
              <div className="flex gap-2">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel className="text-[14px] font-medium text-foreground">
                        Name
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="John Doe"
                          className="h-9 rounded-full border-border bg-background px-3 py-1 text-[14px] shadow-sm focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:outline-none transition-[color,box-shadow]"
                          disabled={loading}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="company"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel className="text-[14px] font-medium text-foreground">
                        {isInvitationField ? 'Organization' : 'Company'}
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="acmecorp"
                          {...getInvitationFieldProps()}
                          {...field}
                        />
                      </FormControl>
                      {isInvitationField && (
                        <p className="text-xs text-muted-foreground">
                          Joining {invitationInfo?.organizationName}
                        </p>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Email Field */}
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[14px] font-medium text-foreground">
                      Email
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="John@acmecorp.com"
                        {...getInvitationFieldProps()}
                        {...field}
                      />
                    </FormControl>
                    {isInvitationField && (
                      <p className="text-xs text-muted-foreground">
                        Pre-filled from your invitation
                      </p>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Password Field */}
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[14px] font-medium text-foreground">
                      Password
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          placeholder="••••••••••••"
                          className="h-9 rounded-full border-border bg-background px-3 py-1 pr-10 text-[14px] shadow-sm focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:outline-none transition-[color,box-shadow]"
                          disabled={loading}
                          {...field}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                          onClick={() => setShowPassword(!showPassword)}
                          disabled={loading}
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <Eye className="h-4 w-4 text-muted-foreground" />
                          )}
                        </Button>
                      </div>
                    </FormControl>
                    <FormDescription className="text-[14px] text-muted-foreground">
                      Minimum 8 characters
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Submit Error */}
              {emailAlreadyExists && (
                <Alert variant="destructive" className="space-y-1">
                  <p className="font-semibold">
                    Looks like you already have an account.
                  </p>
                  <p className="text-sm">
                    Try logging in instead, or reset your password if
                    you&apos;ve forgotten it.
                  </p>
                  <div className="flex gap-3 pt-1">
                    <Link
                      to={routePaths.login}
                      className="text-sm font-medium underline underline-offset-2"
                    >
                      Log in
                    </Link>
                    <Link
                      to={routePaths.forgotPassword}
                      className="text-sm font-medium underline underline-offset-2"
                    >
                      Reset password
                    </Link>
                  </div>
                </Alert>
              )}
              {submitError && !emailAlreadyExists && (
                <Alert variant="destructive">{submitError}</Alert>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full h-9 rounded-full"
                disabled={loading || !isFormComplete}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating Account...
                  </>
                ) : isInvitationField ? (
                  'Accept Invitation & Create Account'
                ) : (
                  'Create Account'
                )}
              </Button>

              {/* Terms */}
              <p className="text-xs text-center text-muted-foreground">
                * By signing up, you agree to our{' '}
                <a
                  href="https://www.blueconic.com/terms"
                  className="underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms of Use
                </a>{' '}
                and acknowledge you've read our{' '}
                <a
                  href="https://www.blueconic.com/privacy-policy"
                  className="underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>
              </p>
            </form>
          </Form>
        </div>
      </div>

      {/* Right side - Animated content showcase (hidden on mobile) */}
      <div className="hidden lg:flex flex-1 bg-[#0f172a] rounded-2xl overflow-hidden flex-col gap-12 items-center justify-center relative">
        {/* Top text with fade transition */}
        <div className="max-w-[350px] w-full text-center space-y-4 relative h-[120px]">
          {contentStates.map((state, index) => (
            <div
              key={state.title}
              className={`absolute inset-0 transition-opacity duration-500 ${
                index === activeSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <p className="text-sm text-blue-400 mb-2">Vwam</p>
              <h2 className="text-2xl font-semibold text-white mb-4">
                {state.title}
              </h2>
              <p className="text-sm text-slate-200">{state.description}</p>
            </div>
          ))}
        </div>

        {/* Content cards with slide and scale transition */}
        <div className="w-full h-[283px] relative flex items-center justify-center overflow-hidden">
          {contentStates.map((state, index) => {
            const offset = index - activeSlide

            let transformClass = ''
            let opacityClass = ''
            let zIndexClass = ''

            // Active card
            if (offset === 0) {
              transformClass = 'translate-x-0 scale-100'
              opacityClass = 'opacity-100'
              zIndexClass = 'z-20'
            }
            // Next card (to the right)
            else if (offset === 1) {
              transformClass = 'translate-x-[550px] scale-90'
              opacityClass = 'opacity-40'
              zIndexClass = 'z-10'
            }
            // Previous card (to the left)
            else if (offset === -1) {
              transformClass = '-translate-x-[550px] scale-90'
              opacityClass = 'opacity-40'
              zIndexClass = 'z-10'
            }
            // Cards further away - hidden off screen
            else if (offset > 1) {
              transformClass = 'translate-x-[calc(2*550px)] scale-90'
              opacityClass = 'opacity-0'
              zIndexClass = 'z-0'
            } else {
              transformClass = '-translate-x-[calc(2*550px)] scale-90'
              opacityClass = 'opacity-0'
              zIndexClass = 'z-0'
            }

            return (
              <div
                key={state.title}
                className={`absolute inset-0 max-w-[550px] mx-auto ${state.bgColor} rounded-2xl overflow-hidden transition-all duration-700 ease-in-out ${transformClass} ${opacityClass} ${zIndexClass}`}
              >
                {state.content}
              </div>
            )
          })}
        </div>

        {/* Navigation */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3 z-30">
          <Button
            variant="ghost"
            size="sm"
            className={`w-6 h-6 p-1 text-white hover:text-white hover:bg-white/10 ${
              activeSlide === 0 ? 'opacity-30 cursor-not-allowed' : ''
            }`}
            onClick={prevSlide}
            disabled={activeSlide === 0}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>

          <div className="flex gap-1">
            {contentStates.map((state, index) => (
              <button
                key={state.title}
                type="button"
                className={`w-2 h-2 rounded-full transition-all ${
                  index === activeSlide ? 'bg-white w-6' : 'bg-white/50'
                }`}
                onClick={() => setActiveSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

          <Button
            variant="ghost"
            size="sm"
            className="w-6 h-6 p-1 text-white hover:text-white hover:bg-white/10"
            onClick={nextSlide}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
